<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';

import {
    type AuctionItemEntity,
    AuctionState,
    getApiAuctionDetailQueryKey,
    LeadIntent,
    useApiUpdateAccountPersonalData,
    type UserMeta,
} from '~/apiClient';
// eslint-disable-next-line regex/invalid
import { UserGatedAction } from '~/components/Login/LoginGatedActionInfo.vue';
import { isAuctionItemEntityWithCurrentPrince } from '~/types/auction.type';
import { NotificationDuration } from '~/types/notifications.type';
import { isAuctionItemEntityWithNegotiation } from '~/types/sale.type';

interface Props {
    auction: AuctionItemEntity;
}
// PROPS
const { auction } = defineProps<Props>();

// COMPOSABLES
const queryClient = useQueryClient();
const { tt, tn } = useTypedI18n();
const { notifyError } = useNotification();
const { data: session, isLoggedIn, getSession } = useAuthUtils();
const { trackAdpAlertView, trackAdpPlaceBid, updateTrackingCompanyMetrics } = useTracking();
const { openLoginModal } = useModals();
const { hasRestrictedNotice, hasInspectionNotice, isBailNeeded, activeAutoBidAmount } = useAuctionStatus(toRef(() => auction));
const inspectionExpiredModalState = useModalState<boolean>();
const bailModalState = useModalState<boolean>();
const bidModalState = useModalState<boolean>();
const { captureLeadContacts, closeLeadModal } = useSdpLead();
const { mutateAsync: mutatePersonalData } = useApiUpdateAccountPersonalData();
const { logError } = useLogs();

// DATA
const hasPlaceBid = computed(() => auction.state === AuctionState.Running);
const hasGoToNegotiation = computed(() => isLoggedIn.value && isAuctionItemEntityWithNegotiation(auction));
const showSection = computed(() => hasRestrictedNotice.value || hasInspectionNotice.value || hasPlaceBid.value || hasGoToNegotiation.value);
const userMeta = computed<UserMeta | undefined>(() => auction.userMeta);
const shouldShowInspectionExpiredModal = computed(
    () =>
        !!userMeta.value?.inspectionExpiredExportRestrictions &&
        (isBailNeeded.value || (userMeta.value.isWhitelisted && userMeta.value.userBids === 0))
);
const bidFlowInProgress = ref(false);
const bidButtonLabel = computed(() => (userMeta.value?.autoBid?.active ? tt(`sdp.Auction.bidButtonActiveAutoBid`) : tt(`sdp.Auction.bidButton`)));

// HANDLERS
const bidFlow = async (): Promise<void> => {
    bidFlowInProgress.value = true;
    trackAdpPlaceBid();

    await (async (): Promise<void> => {
        const customerLeadContacts = await captureLeadContacts({ sale: auction, intentType: LeadIntent.PlaceBid });

        if (!customerLeadContacts) return;

        if (!isLoggedIn.value) {
            if (!(await openLoginModal({ email: customerLeadContacts.email, action: UserGatedAction.ADP_BID }))) return;

            await getSession();
        }

        if (!session.value?.user.phone) {
            mutatePersonalData({ data: { phone: customerLeadContacts.phone } }).catch(error =>
                logError(`Error while saving phone in ADP`, { auctionId: auction.id, userId: session.value?.user.id, apiError: error })
            );
        }

        if (shouldShowInspectionExpiredModal.value) {
            trackAdpAlertView('expired_revision');
            if (!(await inspectionExpiredModalState.open())) return;
        }

        if (isBailNeeded.value) {
            if (!(await bailModalState.open())) return;

            await until(isBailNeeded).toBe(false);
        }

        if (!userMeta.value?.canBid) {
            return;
        }

        if (await bidModalState.open()) {
            await queryClient.refetchQueries({ queryKey: getApiAuctionDetailQueryKey(auction.id), type: 'active' }); // Refetch not strictly necessary, redundant with update via WS
            updateTrackingCompanyMetrics();
        }
    })();

    bidFlowInProgress.value = false;
};

const closeModals = (): void => {
    closeLeadModal();
    inspectionExpiredModalState.cancel();
    bailModalState.cancel();
    bidModalState.cancel();
};

// WATCHERS
watch(isLoggedIn, () => {
    // Reset internal state on login change
    closeModals();
});

watch(
    () => auction.state,
    value => {
        if ([AuctionState.Expired, AuctionState.Canceled].includes(value) && bidFlowInProgress.value) {
            closeModals();
            notifyError({
                title: tt(`sdp.Auction.bid.expiredError.title`),
                description: tt(`sdp.Auction.bid.expiredError.description`),
                duration: NotificationDuration.NoTimeout,
            });
        }
    }
);
</script>

<template>
    <div v-if="showSection" class="flex flex-col justify-center bg-white p-4 lg:p-6">
        <div class="flex flex-col gap-3">
            <SdpSalePanelNoticeRestricted v-if="hasRestrictedNotice" :sale="auction" />
            <template v-else>
                <SdpSalePanelNoticeBase
                    v-if="hasInspectionNotice"
                    :message="tt(`sdp.notice.inspectionExpired`)"
                    icon-name="lucide:triangle-alert"
                    icon-class="text-warning-500"
                />

                <template v-if="hasPlaceBid">
                    <span v-if="activeAutoBidAmount">
                        {{ tt(`sdp.Auction.bid.activeAutoBid`) }}:
                        <span class="text-primary font-medium">
                            {{ tn(activeAutoBidAmount, 'currency') }}
                        </span>
                    </span>

                    <UButton
                        size="lg"
                        :ui="buttonResponsiveSizeUi('lg', 'xl')"
                        :label="bidButtonLabel"
                        block
                        :loading="bidFlowInProgress"
                        @click="bidFlow"
                    />

                    <LazySdpInspectionExpiredModal
                        v-if="shouldShowInspectionExpiredModal"
                        v-model="inspectionExpiredModalState.isOpen"
                        @confirm="inspectionExpiredModalState.confirm(true)"
                    />

                    <LazyAdpBailModal
                        v-if="isBailNeeded"
                        v-model="bailModalState.isOpen"
                        :auction="auction"
                        @success="bailModalState.confirm(true)"
                    />

                    <LazyAdpBidModal
                        v-if="userMeta?.canBid && isAuctionItemEntityWithCurrentPrince(auction)"
                        v-model="bidModalState.isOpen"
                        :auction="auction"
                        @success="bidModalState.confirm(true)"
                    />
                </template>
            </template>

            <SdpSalePanelGoToNegotiation v-if="hasGoToNegotiation" :sale="auction" />
        </div>
    </div>
</template>
